import { frameworks, getGroup, getGroupChildrenTags, Group, GroupTypes, sdgGroupFull, standards } from '@g17eco/core';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { loadSurvey } from '../../../actions/survey';
import { ScopeGroups, SurveyModelMinimalUtrv } from '../../../model/surveyData';
import { useAppSelector } from '../../../reducers';
import { AnalyticsEvents } from '../../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../../services/analytics/AnalyticsService';
import G17Client from '../../../services/G17Client';
import { loadCustomMetricGroupsBySurveyId } from '../../../slice/metricGroupsSlice';
import { DownloadDisplayOptions, DownloadType, VisibilityStatus } from '../../../types/download';
import { HistoricalReportData } from '../../../types/reportData';
import { DownloadButton } from '../../button/DownloadButton';
import Loader from '../../loader';
import { getSGXMetric, getSustainabilityReport } from '../outputs';
import { reportDataWithHistoryScope } from '../reportApi';
import { EnergyConsumptionDataSource } from '../sgx-metric-report/data/EnergyConsumption';
import { GHGEmissionsDataSource } from '../sgx-metric-report/data/GHGEmissions';
import { GHGEmissionsIntensityDataSource } from '../sgx-metric-report/data/GHGEmissionsIntensity';
import { GovernanceDataSource } from '../sgx-metric-report/data/Governance';
import { SocialDataSource } from '../sgx-metric-report/data/Social';
import { WaterConsumptionDataSource } from '../sgx-metric-report/data/WaterConsumption';
import { DownloadSettingModal, DownloadSettingPropType, OptionTitle } from '../../downloads/DownloadSettingModal';
import GenerateCharts from '../sgx-metric-report/GenerateCharts';
import { getDocVisibility, getDownloadScope, getScopeGroupData, SCOPES } from '../sgx-metric-report/downloadScope';
import { handleSettingsChange } from '../../downloads';
import { convertToUtrvStatuses, DownloadSettingsType, SelectedGroup } from '../../downloads/util/downloadReportHandler';
import { UtrvStatus } from '../../../constants/status';
import { addSiteAlert } from '../../../slice/siteAlertsSlice';

interface Props extends DownloadDisplayOptions {
  initiativeId: string;
  surveyId: string;
  disabled?: boolean;
  publicQuestionsOnly?: boolean;
  downloadXlsx?: (code?: string, visibility?: VisibilityStatus) => void;
  downloadCsv?: (code?: string, visibility?: VisibilityStatus) => void;
  code?: string;
  defaultStatus?: UtrvStatus;
  requiredScope?: string,
  modalTitleOptions?: {
    word?: OptionTitle,
    simple?: OptionTitle,
    detailed?: OptionTitle
  }
  totals?: {
    total: number;
    verified: number;
    sentToAssurer: number;
    assured: number;
  };
}

export interface DataSource {
  loaded: boolean;
  width?: number;
  height?: number;
  chart?: string;
}

export interface DataSources {
  ghgEmissions: GHGEmissionsDataSource;
  ghgEmissionsIntensity: GHGEmissionsIntensityDataSource;
  energyConsumption: EnergyConsumptionDataSource;
  waterConsumption: WaterConsumptionDataSource;
  social: SocialDataSource;
  governance: GovernanceDataSource;
}
const initialDataState = {
  loaded: false,
};

const initialDataSourcesState: DataSources = {
  ghgEmissions: initialDataState,
  ghgEmissionsIntensity: initialDataState,
  energyConsumption: initialDataState,
  waterConsumption: initialDataState,
  social: initialDataState,
  governance: initialDataState,
};

interface DownloadData {
  questionData: HistoricalReportData[];
  targets: SurveyModelMinimalUtrv[];
}

const allParents = [...Object.values(standards), ...Object.values(frameworks)];
const SGXTag = 'sgx_metrics';

export const getSDGName = (group: Group) => `Goal ${group.code} ${group.shortName ?? group.name}`

export const getParentGroup = (
  groupType: GroupTypes,
  code: string,
  type: ScopeGroups,
  allParents: Group[],
  getDisplayName?: (group: Group) => string
) => {
  const group = getGroup(groupType, code);
  if (group) {
    return {
      code: group.code,
      name: getDisplayName?.(group) ?? group.shortName ?? group.name,
      checked: true, type
    };
  }
  const parent = allParents.find((group) => getGroupChildrenTags(group).includes(code));
  if (parent) {
    return {
      code: parent.code,
      name: getDisplayName?.(parent) ?? parent.shortName ?? parent.name,
      checked: true,
      type,
    };
  }
  return undefined;
};

export const SustainabilityDownloadButton = ({
  code,
  defaultStatus,
  requiredScope,
  surveyId,
  initiativeId,
  disabled,
  modalTitleOptions,
  totals,
  displayUserInput = false,
  displayTag = false,
}: Props) => {
  const surveyState = useAppSelector((state) => state.survey);
  const { data: metricGroups } = useAppSelector((state) => state.customMetricSurveyGroups);

  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(true);
  const [downloadData, setDownloadData] = useState<DownloadData>({ questionData: [], targets: [] });

  const [dataSourcesState, setData] = useState(initialDataSourcesState);
  const [isLoaded, setLoaded] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [downloadSettings, setDownloadSettings] = useState<DownloadSettingsType>({
    selectedScopes: [], // loaded from loadSurveyScope below
    visibility: VisibilityStatus.ExcludeData,
    status: defaultStatus, // not set for sgx
  });

  const dispatch = useDispatch();
  const analytics = getAnalytics();

  const startDownload = async () => {
    setDownloaded(false);
    setDownloading(true);
    const appliedStatus = downloadSettings.assuranceStatus ?? (downloadSettings.status || defaultStatus);
    const statuses = convertToUtrvStatuses(appliedStatus);

    return reportDataWithHistoryScope(surveyId, {
      scope: getDownloadScope(downloadSettings.selectedScopes),
      visibility: getDocVisibility(downloadSettings.visibility),
      targets: true, // Include targets
      ...statuses,
    })
      .then((data) => {
        setDownloadData({ questionData: data.historical, targets: data.targets });

        return analytics.track(AnalyticsEvents.SurveyDataDownloaded, {
          initiativeId,
          surveyId: surveyId,
          source: 'downloads_page',
          scopeValue: code,
          type: 'docx',
        });
      })
      .catch((e) => console.log(e.message));
  };

  const updateData = useCallback(
    (key: string, data: DataSource) => {
      setData((dataSources) => ({
        ...dataSources,
        [key]: data,
      }));
    },
    [setData]
  );

  const handleCloseModal = () => {
    setOpenSettings(false);
  };

  useEffect(() => {
    if (surveyId !== surveyState.surveyId) {
      dispatch(loadSurvey(surveyId));
      dispatch(loadCustomMetricGroupsBySurveyId(surveyId));
    }
  }, [surveyId, surveyState.surveyId, dispatch]);

  useEffect(() => {
    const allLoaded = Object.keys(dataSourcesState).every((k) => dataSourcesState[k as keyof DataSources].loaded);
    setLoaded(allLoaded);
  }, [dataSourcesState]);

  useEffect(() => {
    if (isLoaded && downloading) {
      setDownloading(false);
      G17Client.getSurveyListItem(surveyId)
        .then(async (survey) => {
          const blueprintContributions = await G17Client.getBlueprintContributions(survey.sourceName);
          return {
            blueprintContributions,
            survey,
          };
        })
        .then(({ survey, blueprintContributions }) => {
          if (code === SGXTag) {
            return getSGXMetric({
              survey,
              dataSources: dataSourcesState,
              questionData: downloadData.questionData,
              targets: downloadData.targets,
              scopeGroupHistoricalData: getScopeGroupData(
                downloadData.questionData,
                downloadSettings.selectedScopes,
                blueprintContributions
              ),
              visibilityStatus: downloadSettings.visibility,
              displayUserInput,
            });
          }
          return getSustainabilityReport({
            survey,
            dataSources: dataSourcesState,
            questionData: downloadData.questionData,
            targets: downloadData.targets,
            scopeGroupHistoricalData: getScopeGroupData(
              downloadData.questionData,
              downloadSettings.selectedScopes,
              blueprintContributions
            ),
            visibilityStatus: downloadSettings.visibility,
            displayUserInput,
          });
        })
        .then(() => {
          setDownloaded(true);
          setData(initialDataSourcesState);
        })
        .catch((e) => console.log(e));
    }
  }, [code, dataSourcesState, surveyId, isLoaded, downloading, downloadData, downloadSettings, displayUserInput]);

  const getSurveyScopeInfo = (codes: string[], type: ScopeGroups): SelectedGroup[] => {
    if (type === ScopeGroups.Custom) {
      return metricGroups
        .filter((metricGroup) => codes.includes(metricGroup._id))
        .map((metricGroup) => {
          return {
            code: metricGroup._id,
            name: metricGroup.groupName,
            universalTrackers: metricGroup.universalTrackers,
            type,
            checked: true,
          };
        });
    }
    if (type === ScopeGroups.Sdg) {
      const sdgGroupsMap = codes.reduce((acc, code) => {
        const group = getParentGroup('sdg', code, type, sdgGroupFull, getSDGName);
        if (group) {
          acc.set(group.code, group);
        }
        return acc;
      }, new Map<string, SelectedGroup>());
      return Array.from(sdgGroupsMap.values());
    }
    const groupsMap = codes.reduce((acc, code) => {
      const group = getParentGroup('standards-and-frameworks', code, type, allParents);
      if (group) {
        acc.set(group.code, group);
      }
      return acc;
    }, new Map<string, SelectedGroup>());
    return Array.from(groupsMap.values());
  };

  const loadSurveyScope = () => {
    if ('scope' in surveyState.data && surveyState.data.scope) {
      const scope = surveyState.data.scope;

      const selectedScopes = SCOPES.reduce((acc, key) => {
        const scopeGroup = getSurveyScopeInfo(scope[key], key);
        return [...acc, ...scopeGroup];
      }, [] as SelectedGroup[]);
      return setDownloadSettings({ ...downloadSettings, selectedScopes });
    }
    setDownloadSettings({ ...downloadSettings, selectedScopes: [] });
  };

  const openDownloadSettings = () => {
    loadSurveyScope();
    setOpenSettings(true);
  };

  const downloadModalProps: DownloadSettingPropType = {
    title: 'Sustainability report',
    titleOptions: {
      ...modalTitleOptions
    },
    requiredScope: requiredScope,
    isOpen: openSettings,
    downloadSettings,
    totals,
    handleClose: handleCloseModal,
    downloadDoc: startDownload,
    handleChange: (event) => handleSettingsChange(event, setDownloadSettings, downloadSettings),
    onDownload: async (type, config) => {
      const appliedStatus = downloadSettings.assuranceStatus ?? (downloadSettings.status || defaultStatus);
      const statuses = convertToUtrvStatuses(appliedStatus);
      switch (type) {
        case DownloadType.SimpleCsv:
        case DownloadType.SimpleXlsx:
          return G17Client.downloadSurveySimple({
            surveyId,
            type: DownloadType.SimpleCsv === type ? 'csv' : 'xlsx',
            downloadScope: {
              scope: getDownloadScope(downloadSettings.selectedScopes),
              visibility: downloadSettings.visibility,
              ...statuses,
              displayUserInput,
              displayTag,
            },
          });
        case DownloadType.Pptx:
          return G17Client.createBackgroundReport({
            surveyId,
            type: 'pptx',
            downloadScope: {
              scope: getDownloadScope(downloadSettings.selectedScopes),
              visibility: downloadSettings.visibility,
              ...statuses,
              displayUserInput,
              displayTag,
            },
            ...config
          })
            .then(() => {
              dispatch(
                addSiteAlert({
                  content: 'Your report is being prepared. You will receive a notification when it is ready to download.',
                  timeout: 10000
                })
              )
            });
        case DownloadType.Xlsx:
        case DownloadType.Csv:
          return G17Client.downloadSurveyData({
            surveyId: surveyId,
            type: type,
            downloadScope: {
              scope: getDownloadScope(downloadSettings.selectedScopes),
              visibility: downloadSettings.visibility,
              ...statuses, // different from sgx
              displayUserInput,
              displayTag,
            },
          });
        default:
          return;
      }
    },
  };

  return (
    <div className='ctl-report-generator mt-5'>
      {!downloaded ? <Loader /> : <></>}
      {downloadData.questionData.length > 0 ? (
        <GenerateCharts
          updateData={updateData}
          questionData={downloadData.questionData}
          visibility={downloadSettings.visibility}
        />
      ) : null}
      <DownloadButton
        className='doc-btn'
        color='secondary'
        outline={true}
        disabled={disabled}
        onClick={openDownloadSettings}
      >
        Download report
        <i className='fas fa-file-download ml-2' />
      </DownloadButton>
      <DownloadSettingModal {...downloadModalProps} />
    </div>
  );
};
